import React, { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { ActionField, ActionType, EditorConfig } from "../types/Actions";
import { Checkbox, Form, Input, Select, Spin, Tooltip } from "antd";
import ActionDefinitionStore from "../stores/ActionDefinitionStore";
import { PackageBase, TaskBase } from "../../tasks/types";
import { Utils } from "../../common/misc/Utils";
import { FormInstance } from "antd/es/form/Form";
import { FormOutlined, MinusCircleOutlined } from "@ant-design/icons";

interface Props {
    store: ActionDefinitionStore;
    index: number;
    form: FormInstance;
    actionType?: ActionType;
    remove: (index: number | number[]) => void;
}

const ActionFieldEditor: React.FC<Props> = ({
    store,
    index,
    form,
    actionType,
    remove,
}) => {
    const fields =
        (Form.useWatch("fields", form) as ActionField[] | undefined) ?? [];
    const currentField = fields[index];
    const fieldType = currentField?.fieldType;
    const fieldName = currentField?.name;
    const isPredefined = currentField?.isPredefined || false;
    const aiProjectValue = fields.find(
        (field) => field?.name === "aiProjectId",
    )?.defaultValue;
    const currentDefaultValue = currentField?.defaultValue;
    const isAiProjectField = fieldName === "aiProjectId";
    const isAiApplicationField = fieldName === "aiAppId";
    const usesAiProjectDropdown = isAiProjectField;
    const usesAiApplicationDropdown = isAiApplicationField;
    const previousFieldTypeRef = useRef(fieldType);
    const hasResolvedAiProjectValue = store.aiProjects.some(
        (project) => project.id === currentDefaultValue,
    );
    const hasResolvedAiApplicationValue = store.aiApplications.some(
        (application) => application.id === currentDefaultValue,
    );
    const isAiProjectValuePending =
        fieldType === "FixedValue" &&
        usesAiProjectDropdown &&
        !!currentDefaultValue &&
        (!store.aiProjectsLoaded || store.aiProjectsLoading);
    const isAiApplicationValuePending =
        fieldType === "FixedValue" &&
        usesAiApplicationDropdown &&
        !!currentDefaultValue &&
        !!aiProjectValue &&
        (store.aiApplicationsLoading ||
            store.aiApplicationsProjectId !== aiProjectValue);
    const isAiProjectValueUnavailable =
        fieldType === "FixedValue" &&
        usesAiProjectDropdown &&
        !!currentDefaultValue &&
        store.aiProjectsLoaded &&
        !store.aiProjectsLoading &&
        !hasResolvedAiProjectValue;
    const isAiApplicationValueUnavailable =
        fieldType === "FixedValue" &&
        usesAiApplicationDropdown &&
        !!currentDefaultValue &&
        !!aiProjectValue &&
        !store.aiApplicationsLoading &&
        (store.aiApplicationsProjectId === aiProjectValue ||
            isAiProjectValueUnavailable) &&
        !hasResolvedAiApplicationValue;

    useEffect(() => {
        const previousFieldType = previousFieldTypeRef.current;
        previousFieldTypeRef.current = fieldType;

        if (
            previousFieldType === fieldType ||
            !form.isFieldTouched(["fields", index, "fieldType"])
        ) {
            return;
        }

        form.setFieldValue(["fields", index, "defaultValue"], undefined);

        if (isAiProjectField) {
            const aiAppIndex = fields.findIndex(
                (field) => field?.name === "aiAppId",
            );

            if (aiAppIndex >= 0) {
                form.setFieldValue(
                    ["fields", aiAppIndex, "defaultValue"],
                    undefined,
                );
            }

            store.clearAiApplications();
        }
    }, [fieldType, fields, form, index, isAiProjectField, store]);

    useEffect(() => {
        if (fieldType === "AttachmentProperty" && !actionType?.isAttachment) {
            form.setFieldValue(["fields", index, "fieldType"], undefined);
            form.setFieldValue(["fields", index, "defaultValue"], undefined);
        }
    }, [actionType, fieldType, index, form]);

    const onEditorChange = (ec: EditorConfig) => {
        form.setFieldValue(["fields", index, "editor"], ec);
    };

    const fieldTypes = [
        { value: "FixedValue", label: "Fixed value" },
        { value: "TaskProperty", label: "Task property" },
        { value: "Metadata", label: "Metadata field" },
        {
            value: "AttachmentProperty",
            label: "Attachment field",
            disabled: !(actionType?.isAttachment ?? false),
        },
        { value: "UserInput", label: "User input" },
    ];

    const fieldTypeValues: Record<string, string> = {
        TaskProperty: "Task Property",
        Metadata: "Metadata Field",
        AttachmentProperty: "Attachment Field",
        FixedValue: "Value",
    };

    const taskProperties = Object.keys(new TaskBase())
        .map((key) => Utils.capitalizeWord(key))
        .map((key) => ({ label: key, value: key }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const metadataFields = store.metadata.map((m) => ({
        label: m.isMultiValue ? `${m.title} (select at execution)` : m.title,
        value: m.name,
    }));

    const attachmentProperties = Object.keys(new PackageBase())
        .map((key) => Utils.capitalizeWord(key))
        .map((key) => ({ label: key, value: key }))
        .sort((a, b) => a.label.localeCompare(b.label));

    const aiProjectOptions = store.aiProjects.map((project) => ({
        label: project.name,
        value: project.id,
    }));

    const aiApplicationOptions = store.aiApplications.map((application) => ({
        label: application.name,
        value: application.id,
    }));

    const getFieldOptions = () => {
        switch (fieldType) {
            case "TaskProperty":
                return taskProperties;
            case "Metadata":
                return metadataFields;
            case "AttachmentProperty":
                return attachmentProperties;
            default:
                return [];
        }
    };

    const handleAiProjectChange = (value: string) => {
        const aiAppIndex = fields.findIndex(
            (field) => field?.name === "aiAppId",
        );

        if (aiAppIndex >= 0 && value !== aiProjectValue) {
            form.setFieldValue(
                ["fields", aiAppIndex, "defaultValue"],
                undefined,
            );
        }

        store.clearAiApplications();
    };

    const renderDefaultValueEditor = () => {
        if (
            fieldType !== undefined &&
            ["Metadata", "TaskProperty", "AttachmentProperty"].includes(
                fieldType,
            )
        ) {
            return (
                <Select
                    options={getFieldOptions()}
                    aria-label="Default value"
                    style={{ width: "100%" }}
                />
            );
        }

        if (fieldType === "FixedValue" && usesAiProjectDropdown) {
            return (
                <Select
                    showSearch
                    optionFilterProp="label"
                    options={aiProjectOptions}
                    aria-label="Default value"
                    style={{ width: "100%" }}
                    loading={store.aiProjectsLoading}
                    notFoundContent={
                        store.aiProjectsLoading ? (
                            <Spin size="small" />
                        ) : undefined
                    }
                    onChange={handleAiProjectChange}
                />
            );
        }

        if (fieldType === "FixedValue" && usesAiApplicationDropdown) {
            return (
                <Select
                    showSearch
                    optionFilterProp="label"
                    options={aiApplicationOptions}
                    aria-label="Default value"
                    style={{ width: "100%" }}
                    disabled={!aiProjectValue}
                    loading={store.aiApplicationsLoading}
                    notFoundContent={
                        store.aiApplicationsLoading ? (
                            <Spin size="small" />
                        ) : (
                            "No applications available"
                        )
                    }
                />
            );
        }

        return <Input style={{ width: "100%" }} aria-label="Default value" />;
    };

    const getDefaultValueProps = (value?: string) => {
        if (
            isAiProjectValuePending ||
            isAiApplicationValuePending ||
            isAiProjectValueUnavailable ||
            isAiApplicationValueUnavailable
        ) {
            return { value: undefined };
        }

        return { value };
    };

    const unavailableHelpMessage = isAiProjectValueUnavailable
        ? "Selected AI project was removed."
        : isAiApplicationValueUnavailable
          ? "Selected AI application was removed or deactivated."
          : undefined;

    return (
        <div className="action-field-editor-row">
            <Form.Item name={[index, "isPredefined"]} noStyle>
                <Input type="hidden" />
            </Form.Item>
            <Form.Item
                className="action-field-cell action-field-cell-title"
                name={[index, "title"]}
            >
                <Input
                    aria-label="Title"
                    style={{ width: "100%" }}
                    readOnly={isPredefined}
                />
            </Form.Item>
            <Form.Item
                className="action-field-cell action-field-cell-name"
                name={[index, "name"]}
                rules={[{ required: true, message: "Name is required" }]}
            >
                <Input
                    aria-label="Field name"
                    style={{ width: "100%" }}
                    readOnly={isPredefined}
                />
            </Form.Item>
            <Form.Item
                className="action-field-cell action-field-cell-type"
                name={[index, "fieldType"]}
                rules={[{ required: true, message: "Type is required" }]}
            >
                <Select
                    options={fieldTypes}
                    aria-label="Field type"
                    style={{ width: "100%" }}
                />
            </Form.Item>
            <Form.Item
                className="action-field-cell action-field-cell-default"
                name={[index, "defaultValue"]}
                getValueProps={getDefaultValueProps}
                validateStatus={unavailableHelpMessage ? "error" : undefined}
                help={unavailableHelpMessage}
                rules={[
                    {
                        required:
                            fieldType !== undefined &&
                            ["TaskProperty", "Metadata", "FixedValue"].includes(
                                fieldType,
                            ),
                        message: `${fieldType ? fieldTypeValues[fieldType] : "Value"} is required`,
                    },
                ]}
            >
                {renderDefaultValueEditor()}
            </Form.Item>
            <Form.Item
                className="action-field-cell action-field-cell-required"
                name={[index, "isRequired"]}
                valuePropName="checked"
            >
                <Checkbox aria-label="Required" disabled={isPredefined} />
            </Form.Item>
            <div className="action-field-cell action-field-cell-actions">
                {fieldType === "UserInput" && (
                    <Tooltip title="Configure input" placement="bottom">
                        <FormOutlined
                            className="action-row-icon action-row-icon-secondary"
                            onClick={() => {
                                store.setEditInputDialogVisible(
                                    true,
                                    currentField?.editor,
                                    onEditorChange,
                                );
                            }}
                        />
                    </Tooltip>
                )}
                {isPredefined ? null : (
                    <MinusCircleOutlined
                        className="action-row-icon action-row-icon-delete"
                        onClick={() => remove(index)}
                    />
                )}
            </div>
        </div>
    );
};

export default observer(ActionFieldEditor);
